<template>
    <div v-show="modal" id="aiSettingsModal" class="modal" @click.self="closeModal">
        <div class="modal-content">
            <div class="modal-header">
                <div class="flex w-full justify-between items-center">
                    <h1>Nastavenia AI generovania</h1>
                    <svg @click="closeModal" xmlns="http://www.w3.org/2000/svg" width="33" height="33"
                        viewBox="0 0 33 33" fill="none">
                        <path d="M8.25 24.75L24.75 8.25M8.25 8.25L24.75 24.75" stroke="#5C5C5C" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
                <p class="intro">Prispôsobte si, ako AI generuje popisy vašich položiek.</p>
            </div>
            <div class="modal-body">
                <div class="main">
                    <div class="settings-content">
                        <!-- Tón popisu -->
                        <div class="setting-group">
                            <label class="setting-label">Tón popisu</label>
                            <div class="select-wrapper">
                                <select v-model="localSettings.tone" class="setting-select">
                                    <option value="selling">Predajný</option>
                                    <option value="professional">Profesionálny</option>
                                    <option value="friendly">Priateľský</option>
                                    <option value="luxury">Luxusný / Prémiový</option>
                                </select>
                            </div>
                        </div>

                        <!-- Dĺžka popisu -->
                        <div class="setting-group">
                            <label class="setting-label">Dĺžka popisu</label>
                            <div class="radio-group">
                                <label class="radio-option" :class="{ active: localSettings.length === 'short' }">
                                    <input type="radio" v-model="localSettings.length" value="short" />
                                    <span class="radio-text">Krátky</span>
                                    <span class="radio-desc">2-3 vety</span>
                                </label>
                                <label class="radio-option" :class="{ active: localSettings.length === 'medium' }">
                                    <input type="radio" v-model="localSettings.length" value="medium" />
                                    <span class="radio-text">Stredný</span>
                                    <span class="radio-desc">3-5 viet</span>
                                </label>
                                <label class="radio-option" :class="{ active: localSettings.length === 'long' }">
                                    <input type="radio" v-model="localSettings.length" value="long" />
                                    <span class="radio-text">Dlhší</span>
                                    <span class="radio-desc">5-7 viet</span>
                                </label>
                            </div>
                        </div>

                        <!-- Toggles -->
                        <div class="setting-group">
                            <label class="setting-label">Zahrnúť v popise</label>
                            <div class="toggle-group">
                                <label class="toggle-option">
                                    <input type="checkbox" v-model="localSettings.includeAdvantages" />
                                    <span class="toggle-switch"></span>
                                    <span class="toggle-text">Výhody produktu/služby</span>
                                    <span class="info-tooltip" data-tooltip="AI automaticky identifikuje a zdôrazní kľúčové výhody vášho produktu alebo služby v popise.">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="16" x2="12" y2="12"></line>
                                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                        </svg>
                                    </span>
                                </label>
                                <label class="toggle-option">
                                    <input type="checkbox" v-model="localSettings.includeCta" />
                                    <span class="toggle-switch"></span>
                                    <span class="toggle-text">Výzvu k akcii (call-to-action)</span>
                                    <span class="info-tooltip" data-tooltip="Na koniec popisu pridá výzvu k akcii, napr. 'Objednajte ešte dnes!' alebo 'Kontaktujte nás pre viac informácií.'">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="16" x2="12" y2="12"></line>
                                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                        </svg>
                                    </span>
                                </label>
                            </div>
                        </div>

                        <!-- Vlastné inštrukcie -->
                        <div class="setting-group">
                            <div class="flex items-center justify-between">
                                <label class="setting-label">Vlastné inštrukcie <span class="optional">(voliteľné)</span></label>
                                <button
                                    v-if="lastCustomInstructions"
                                    type="button"
                                    class="btn-link"
                                    @click="applyLastCustomInstructions"
                                >
                                    Použiť posledné použité
                                </button>
                            </div>
                            <textarea
                                v-model="localSettings.customInstructions"
                                class="setting-textarea"
                                placeholder="Napr.: Zameraj sa na ekológiu, spomeň záruku, použi formálny jazyk..."
                                rows="3"
                            ></textarea>
                            <span class="char-count">{{ (localSettings.customInstructions || '').length }}/200</span>
                        </div>
                    </div>
                </div>

                <div class="button-row">
                    <button class="btn-secondary" @click="resetToDefaults" :disabled="saving">Obnoviť predvolené</button>
                    <button class="btn-primary" @click="saveAndGenerateDescription" :disabled="saving || loading">
                        {{ saving ? 'Generujem...' : 'Vygenerovať popis cez AI' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AiSettingsModal',
    props: {
        open: {
            type: Boolean,
            default: false
        },
        entry: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            modal: false,
            loading: false,
            saving: false,
            loadingGenerateDescription: false,
            localSettings: this.getDefaultSettings(),
            lastCustomInstructions: ''
        };
    },
    watch: {
        open(val) {
            if (val) {
                this.openModal();
            }
        }
    },
    methods: {
        getDefaultSettings() {
            return {
                tone: 'selling',
                length: 'medium',
                includeAdvantages: true,
                includeCta: false,
                customInstructions: ''
            };
        },
        async openModal() {
            this.modal = true;
            document.documentElement.style.overflow = 'hidden';
            await this.loadSettings();
            // vždy začni s prázdnymi vlastnými inštrukciami
            this.localSettings.customInstructions = '';
        },
        closeModal() {
            this.modal = false;
            document.documentElement.style.overflow = 'auto';
            this.$emit('close');
        },
        async loadSettings() {
            this.loading = true;
            try {
                const response = await axios.get('/api/ai/settings');
                // Check if response is HTML (indicates auth/routing issue)
                if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE')) {
                    console.error('API returned HTML instead of JSON - possible auth issue');
                    return;
                }
                if (response.data.settings) {
                    const settings = response.data.settings;
                    this.localSettings = { ...this.getDefaultSettings(), ...settings };
                    const custom = (settings.customInstructions || '').trim();
                    if (custom) {
                        this.lastCustomInstructions = custom;
                    }
                    // textarea zostáva prázdna, aj keď máme posledné inštrukcie
                    this.localSettings.customInstructions = '';
                }
            } catch (e) {
                console.error('Failed to load AI settings:', e.response?.data || e);
            } finally {
                this.loading = false;
            }
        },
        saveAndGenerateDescription() {
            // Limit custom instructions to 200 chars
            if (this.localSettings.customInstructions) {
                this.localSettings.customInstructions = this.localSettings.customInstructions.substring(0, 200);
            }

            const custom = (this.localSettings.customInstructions || '').trim();

            // payload pre aktuálne generovanie (AI vždy dostane presne to, čo je v textarea – môže byť aj prázdne)
            const payloadForAi = {
                ...this.getDefaultSettings(),
                ...this.localSettings,
                customInstructions: custom
            };

            // payload pre ukladanie nastavení (customInstructions iba ak nie je prázdne)
            const base = { ...this.getDefaultSettings(), ...this.localSettings };
            if (custom) {
                base.customInstructions = custom;
                this.lastCustomInstructions = custom;
            } else {
                delete base.customInstructions;
            }
            const payloadForSave = base;

            // Okamžite informujeme parent komponent:
            // - aby si uložil lokálne nastavenia (bez prázdnych inštrukcií)
            // - aby rovno spustil generovanie popisu s aktuálnymi inštrukciami (môžu byť aj prázdne)
            this.$emit('save', { ...payloadForSave });
            this.$emit('generate', { ...payloadForAi });

            // Modal zatvoríme hneď, UX beží ďalej v pozadí
            this.closeModal();

            // Ukladanie nastavení na server beží na pozadí
            this.saving = true;
            axios.post('/api/ai/settings', payloadForSave)
                .catch((e) => {
                    console.error('Failed to save AI settings:', e);
                    this.$toast.error('Nepodarilo sa uložiť nastavenia', { timeout: 2000 });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        applyLastCustomInstructions() {
            if (this.lastCustomInstructions) {
                this.localSettings.customInstructions = this.lastCustomInstructions;
            }
        },
        resetToDefaults() {
            this.localSettings = this.getDefaultSettings();
        }
    }
};
</script>

<style lang="scss" scoped>
.modal {
    display: block;
    position: fixed;
    z-index: 1089;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: auto;
    background: rgba(33, 37, 65, 0.89);
    backdrop-filter: blur(5px);

    .modal-content {
        margin: 60px auto 60px auto;
        background: #F7F7F7;
        width: 520px;
        border-radius: 4px;
        box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.05);
        padding-bottom: 25px;

        @media (max-width: 600px) {
            width: 95% !important;
            margin: 30px auto 30px auto;
        }


        .btn-link {
                font-family: Roboto, sans-serif;
                font-size: 13px;
                color: #2ED8B6;
                background: transparent;
                border: none;
                cursor: pointer;
                padding: 0 8px 0 0;
                text-decoration: underline;

                &:hover {
                    color: #23caa9;
                }
            }
        .modal-header {
            margin: 25px auto 0 auto;
            width: 90%;
            display: flex;
            flex-direction: column;
            align-items: start;
            justify-content: space-between;
            gap: 13px;
            padding: 0;
            border: none;

            h1 {
                text-align: left;
                font-family: Roboto, sans-serif;
                font-size: 22px;
                font-style: normal;
                font-weight: 400;
                line-height: normal;
                color: #212541;
            }

            svg {
                transition: transform .3s;
                cursor: pointer;

                &:hover {
                    transform: scale(1.2);
                }
            }
            p{
                margin-bottom: 0;
            }
        }

        .modal-body {
            padding: 0;

            .intro {
                width: 90%;
                margin: 15px auto 0 auto;
                font-family: 'Roboto', sans-serif;
                font-size: 14px;
                color: #9598A7;
            }

            .main {
                position: relative;
                width: 90%;
                margin: 0 auto;
                border-radius: 4px;
                background: #FFF;
                box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.05);
                padding: 20px;
                margin-top: 15px;

                .settings-content {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }
            }

            .button-row {
                width: 90%;
                margin: 20px auto 0 auto;
                display: flex;
                justify-content: space-between;
                gap: 12px;

                .btn-secondary {
                    flex: 1;
                    padding: 12px 16px;
                    background: #fff;
                    border: 1px solid #dde3e8;
                    border-radius: 4px;
                    font-family: Roboto, sans-serif;
                    font-size: 14px;
                    font-weight: 500;
                    color: #666;
                    cursor: pointer;
                    transition: all 0.2s;

                    &:hover {
                        background: #f5f5f5;
                        border-color: #ccc;
                    }
                }

                .btn-primary {
                    flex: 1;
                    padding: 12px 16px;
                    background: #2ED8B6;
                    border: none;
                    border-radius: 4px;
                    font-family: Roboto, sans-serif;
                    font-size: 14px;
                    font-weight: 500;
                    color: #fff;
                    cursor: pointer;
                    transition: all 0.2s;

                    &:hover {
                        background: #23caa9;
                    }
                }
            }
        }
    }
}

.setting-group {
    .setting-label {
        display: block;
        font-family: 'Roboto', sans-serif;
        font-weight: 600;
        font-size: 13px;
        color: #212541;
        margin-bottom: 8px;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        .optional {
            font-weight: 400;
            text-transform: none;
            color: #9598A7;
            font-size: 12px;
        }
    }
}

.select-wrapper {
    position: relative;

    &::after {
        content: '';
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid #666;
        pointer-events: none;
    }
}

.setting-select {
    width: 100%;
    padding: 10px 35px 10px 12px;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    color: #212541;
    background: #fff;
    border: 1px solid #dde3e8;
    border-radius: 4px;
    cursor: pointer;
    appearance: none;
    transition: border-color 0.2s;

    &:hover {
        border-color: #2ED8B6;
    }

    &:focus {
        outline: none;
        border-color: #2ED8B6;
    }
}

.radio-group {
    display: flex;
    gap: 10px;

    @media (max-width: 500px) {
        flex-direction: column;
    }
}

.radio-option {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 8px;
    background: #fff;
    border: 1px solid #dde3e8;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;

    input {
        display: none;
    }

    &:hover {
        border-color: #2ED8B6;
    }

    &.active {
        border-color: #2ED8B6;
        background: rgba(46, 216, 182, 0.05);
    }

    .radio-text {
        font-family: 'Roboto', sans-serif;
        font-size: 14px;
        font-weight: 500;
        color: #212541;
    }

    .radio-desc {
        font-family: 'Roboto', sans-serif;
        font-size: 12px;
        color: #9598A7;
        margin-top: 2px;
    }
}

.toggle-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.toggle-option {
    display: flex;
    align-items: center;
    cursor: pointer;

    input {
        display: none;
    }

    .toggle-switch {
        position: relative;
        width: 40px;
        height: 22px;
        background: #dde3e8;
        border-radius: 11px;
        margin-right: 10px;
        transition: background 0.2s;

        &::after {
            content: '';
            position: absolute;
            top: 2px;
            left: 2px;
            width: 18px;
            height: 18px;
            background: #fff;
            border-radius: 50%;
            transition: transform 0.2s;
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
    }

    input:checked + .toggle-switch {
        background: #2ED8B6;

        &::after {
            transform: translateX(18px);
        }
    }

    .toggle-text {
        font-family: 'Roboto', sans-serif;
        font-size: 14px;
        color: #212541;
    }

    .info-tooltip {
        position: relative;
        display: inline-flex;
        align-items: center;
        margin-left: 6px;
        color: #9598A7;
        cursor: help;

        &:hover {
            color: #2ED8B6;
        }

        &::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: calc(100% + 8px);
            left: 50%;
            transform: translateX(-50%);
            width: 220px;
            padding: 10px 12px;
            background: #212541;
            color: #fff;
            font-family: 'Roboto', sans-serif;
            font-size: 12px;
            line-height: 1.4;
            border-radius: 4px;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.2s, visibility 0.2s;
            z-index: 100;
            pointer-events: none;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        &::before {
            content: '';
            position: absolute;
            bottom: calc(100% + 2px);
            left: 50%;
            transform: translateX(-50%);
            border: 6px solid transparent;
            border-top-color: #212541;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.2s, visibility 0.2s;
            z-index: 100;
        }

        &:hover::after,
        &:hover::before {
            opacity: 1;
            visibility: visible;
        }
    }
}

.setting-textarea {
    width: 100%;
    padding: 10px 12px;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    color: #212541;
    background: #fff;
    border: 1px solid #dde3e8;
    border-radius: 4px;
    resize: vertical;
    min-height: 70px;
    transition: border-color 0.2s;

    &::placeholder {
        color: #b0b0b0;
    }

    &:hover {
        border-color: #2ED8B6;
    }

    &:focus {
        outline: none;
        border-color: #2ED8B6;
    }
}

.char-count {
    display: block;
    text-align: right;
    font-family: 'Roboto', sans-serif;
    font-size: 11px;
    color: #9598A7;
    margin-top: 4px;
}
</style>
